import { useQuery } from '@tanstack/react-query';
import { History, KeyRound, Server as ServerIcon, SquareTerminal, TriangleAlert } from 'lucide-react';
import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Panel, SectionHeader } from '@/components/surface/ui';
import { Alert } from '@/components/ui/alert';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { AUTOMATION_CATALOG_KEY, RUNS_KEY, automationApi } from '@/lib/automation-api';
import { countOf, usageLevel } from '@/lib/trial';
import { SubscriptionNotice } from '@/modules/billing/SubscriptionNotice';
import { LaunchPanel } from '@/modules/automation/LaunchPanel';
import { SAFE_STRATEGY, initialSelection, isCompleteSelection } from '@/modules/automation/parameters';
import { RecentRuns } from '@/modules/automation/RecentRuns';
import { TargetPicker } from '@/modules/automation/TargetPicker';
import { Field, PageHeader, Segmented } from '@/modules/automation/ui';
import type { RunAs, RunStrategy, TargetPreview, TargetSelection } from '@/types/api';

const TIME_LIMITS = [
  { value: 60, label: '1 minute' },
  { value: 300, label: '5 minutes' },
  { value: 600, label: '10 minutes' },
  { value: 1800, label: '30 minutes' },
  { value: 3600, label: '1 hour' },
];

/**
 * One command, on one server or on many, with its output from each as it arrives.
 *
 * ## Not a terminal
 *
 * There is no session, no prompt and nothing interactive: a command runs to completion on each
 * server, exactly as typed, and its output, exit code and duration become a run like any other. A
 * command that waits for input gets none and ends. That is what makes it safe to aim at fifty servers,
 * and what makes the record of it worth keeping.
 *
 * ## Careful by default
 *
 * Unlike a playbook, a command has no dry run — there is no way to know what an arbitrary command
 * would change — so the canary matters more here, and it is on unless turned off.
 */
export function CommandRunnerPage() {
  const [searchParams] = useSearchParams();
  const from = searchParams.get('from');
  const commandId = useId();
  const limitId = useId();

  const [command, setCommand] = useState('');
  const [runAs, setRunAs] = useState<RunAs>('root');
  const [timeout, setTimeoutSeconds] = useState(600);
  const [selection, setSelection] = useState<TargetSelection>(() => initialSelection(searchParams));
  const [strategy, setStrategy] = useState<RunStrategy>(SAFE_STRATEGY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [preview, setPreview] = useState<TargetPreview | null>(null);

  // "Run again" from a previous command run: its command, account and limit, never its servers,
  // which are chosen again deliberately.
  const previous = useQuery({
    queryKey: ['automation', 'runs', from, 'prefill'],
    queryFn: () => automationApi.getRun(from ?? ''),
    enabled: from !== null && from !== '',
    staleTime: Infinity,
  });

  useEffect(() => {
    const run = previous.data?.run;

    if (run?.kind === 'command') {
      setCommand(run.command);
      setRunAs(run.run_as);
      setTimeoutSeconds(run.parameters.timeout_seconds ?? 600);
    }
  }, [previous.data]);

  const onPreview = useCallback((next: TargetPreview | null) => setPreview(next), []);
  const serverCount = preview !== null && preview.problems.length === 0 ? preview.servers.length : null;

  // Null on every plan without a ceiling; a number only where commands are counted, which is the
  // free trial unless an operator has set one.
  const catalog = useQuery({ queryKey: AUTOMATION_CATALOG_KEY, queryFn: () => automationApi.catalog(), staleTime: 60_000 });
  const allowance = catalog.data?.command_runs ?? null;
  const allowanceLevel = allowance === null ? 'ok' : usageLevel(allowance.used, allowance.limit);

  const blockedReason = useMemo(() => {
    if (allowance !== null && allowance.remaining <= 0) {
      return allowance.trial
        ? 'The Command Runner runs in the free trial are used up.'
        : 'The Command Runner runs your plan includes are used up.';
    }
    if (command.trim() === '') return 'Type the command to run.';
    if (!isCompleteSelection(selection)) return 'Choose the servers to run it on.';
    if (preview !== null && preview.problems.length > 0) return preview.problems[0] ?? null;
    return null;
  }, [allowance, command, selection, preview]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Command Runner"
        description="Run a command on the servers you choose and watch every server's output as it arrives. Each command runs to completion as typed; nothing is interactive, and every run is kept in the history."
      />

      <SubscriptionNotice />

      {/* Said before anybody types a command, not after they press Run. Quiet while there are runs
          to spare; a plan is suggested only once they are nearly or entirely used. */}
      {allowance !== null && (
        <Alert variant={allowanceLevel === 'ok' ? 'info' : 'warning'}>
          {allowanceLevel === 'reached'
            ? `All ${countOf(allowance.limit, 'Command Runner run', 'Command Runner runs')} ${allowance.trial ? 'in the free trial' : 'your plan includes'} are used. Playbooks and templates still work.`
            : `${countOf(allowance.remaining, 'Command Runner run', 'Command Runner runs')} left of ${allowance.limit} ${allowance.trial ? 'in the free trial' : 'on your plan'}. Each command counts once, however many servers it runs on; playbooks and templates do not count.`}
          {allowanceLevel !== 'ok' && (
            <>
              {' '}
              <Link to="/billing" className="font-medium underline underline-offset-2">
                {allowance.trial ? 'Choose a plan for unlimited commands' : 'Go to billing'}
              </Link>
              .
            </>
          )}
        </Alert>
      )}

      <div className="grid grid-cols-1 items-start gap-6 xl:grid-cols-[minmax(0,1fr)_380px]">
        <div className="min-w-0 space-y-6">
          <Panel>
            <SectionHeader icon={SquareTerminal} iconTone="primary" title="Command" />
            <div className="space-y-5 px-5 pb-5">
              <Field label="Command" htmlFor={commandId} error={errors.command}>
                <Textarea
                  id={commandId}
                  value={command}
                  onChange={(event) => setCommand(event.target.value)}
                  className="min-h-36 font-mono text-[13px] leading-6"
                  spellCheck={false}
                  autoCapitalize="off"
                  autoCorrect="off"
                />
              </Field>

              <p className="flex items-start gap-2 rounded-lg bg-warning/[0.07] px-3 py-2 text-[13px] dark:bg-warning/[0.08]">
                <KeyRound className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden />
                Commands and everything they print are kept in the run history, where anyone on your account can read them. Do not
                put passwords, tokens or keys in a command.
              </p>

              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <Field
                  label="Run as"
                  help={
                    runAs === 'root'
                      ? 'As root: directly when the platform signs in as root, otherwise through sudo.'
                      : 'As the account the platform signs in as, without sudo. Works on servers where that account cannot become root.'
                  }
                  error={errors.run_as}
                >
                  <Segmented<RunAs>
                    ariaLabel="Run as"
                    value={runAs}
                    onChange={setRunAs}
                    options={[
                      { value: 'root', label: 'Root' },
                      { value: 'user', label: 'Signed-in account' },
                    ]}
                  />
                </Field>

                <Field label="Time limit per server" htmlFor={limitId} help="A command still running then is stopped on the server." error={errors.timeout_seconds}>
                  <Select id={limitId} value={timeout} onChange={(event) => setTimeoutSeconds(Number(event.target.value))}>
                    {TIME_LIMITS.map((limit) => (
                      <option key={limit.value} value={limit.value}>
                        {limit.label}
                      </option>
                    ))}
                  </Select>
                </Field>
              </div>

              {errors.mode !== undefined && (
                <p className="flex items-center gap-2 text-sm text-danger">
                  <TriangleAlert className="size-4" aria-hidden /> {errors.mode}
                </p>
              )}
            </div>
          </Panel>

          <Panel>
            <SectionHeader icon={ServerIcon} title="Servers" />
            <div className="px-5 pb-5">
              <TargetPicker
                value={selection}
                onChange={setSelection}
                kind="command"
                subject=""
                error={errors['targets.values'] ?? errors['targets.mode']}
                onPreview={onPreview}
              />
            </div>
          </Panel>
        </div>

        <div className="space-y-6 xl:sticky xl:top-20">
          <LaunchPanel
            strategy={strategy}
            onStrategyChange={setStrategy}
            serverCount={serverCount}
            allowDryRun={false}
            build={(mode) => ({
              kind: 'command',
              command,
              run_as: runAs,
              timeout_seconds: timeout,
              mode,
              strategy,
              targets: selection,
            })}
            onFieldErrors={setErrors}
            blockedReason={blockedReason}
            runLabel="Run command"
          />

          <Panel>
            <SectionHeader
              icon={History}
              title="Recent commands"
              actions={
                <Link to="/automation/runs?kind=command" className="text-xs font-medium text-primary hover:underline">
                  All
                </Link>
              }
            />
            <RecentRuns queryKey={[...RUNS_KEY, 'command']} filters={{ kind: 'command', limit: 6 }} empty="No commands have run yet." />
          </Panel>
        </div>
      </div>
    </div>
  );
}
